import { Component, Inject, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ReassignKamService } from '@app/region-management/services/reassign-kam.service';
import { RegionManagementDataService } from '@app/region-management/services/region-management-data.service';
import { Item, ListTreeComponent, Node } from '@app/shared/components/list-tree/list-tree.component';
import { Strings } from '@app/shared/constants';
import { RegionMunicipalitiesService } from '@app/shared/services/api/region-municipalities.service';
import { Region, RegionService } from '@app/shared/services/api/regions.service';
import { AuthService } from '@app/shared/services/auth.service';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { listToTree } from '@app/shared/utility';
import { find, isEmpty, isNil, isPlainObject, isString, orderBy, toLower, trim } from 'lodash';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, mergeMap, startWith, switchMap, tap } from 'rxjs/operators';

export class KamErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null): boolean {
    return !!(control && control.invalid && (control.dirty || control.touched));
  }
}

@Component({
  selector: 'app-orphan-municipality-dialog',
  templateUrl: './orphan-municipality-dialog.component.html',
  styleUrls: ['./orphan-municipality-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class OrphanMunicipalityDialogComponent implements OnInit {
  isValidKam = (control: AbstractControl): ValidationErrors | null => {
    return (control.dirty || control.touched) && typeof control.value === 'string' ? { valid: false } : null;
  };

  modalLoader = false;
  municipality: any;
  backOfficeRegions: any[] = [];
  serviceRegions: any[] = [];
  adminRegions: any[] = [];
  @ViewChild('backOfficeRegionTree')
  backOfficeRegionTree: ListTreeComponent | undefined;
  @ViewChild('serviceRegionTree')
  serviceRegionTree: ListTreeComponent | undefined;
  @ViewChild('adminRegionTree')
  adminRegionTree: ListTreeComponent | undefined;
  backOfficeSelected = false;
  serviceRegionSelected = false;
  adminRegionSelected = false;
  regionTypes: any[] = [];
  filteredKams: Observable<any[]> = of([]);
  newKamControl = new FormControl('', [this.isValidKam]);
  kamErrorMatcher = new KamErrorStateMatcher();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private matDialogRef: MatDialogRef<OrphanMunicipalityDialogComponent>,
    private regionsService: RegionService,
    private authService: AuthService,
    private regionMunicipalityService: RegionMunicipalitiesService,
    private reassignKam: ReassignKamService,
    private overlayLoader: OverlayLoaderService,
    private regionManagementData: RegionManagementDataService
  ) {}

  ngOnInit() {
    this.modalLoader = true;
    this.municipality = this.data.municipality;
    combineLatest([this.regionManagementData.regionTypes$, this.regionManagementData.regions$, this.regionManagementData.activeKams$]).subscribe(
      (data: any[]) => {
        data[0] = orderBy(data[0], [(x: any) => (x.name ? x.name.toLowerCase() : x.name)], 'asc');
        data[1] = orderBy(data[1], [(x: any) => (x.name ? x.name.toLowerCase() : x.name)], 'asc');

        this.regionTypes = data[0];
        const backOfficeRegionType = find(this.regionTypes, (a: Region) => a.code === 'BOR');
        const serviceRegionType = find(this.regionTypes, (a: Region) => a.code === 'SR');
        const kamRegionType = find(this.regionTypes, (a: Region) => a.code === 'SKR');
        const customerServiceRegionType = find(this.regionTypes, (a: Region) => a.code === 'CSR');
        const adminRegionType = find(this.regionTypes, (a: Region) => a.code === 'AM');
        const regions = data[1].map((a: { [key: string]: any }) => {
          a['selectable'] =
            a.regionTypeId === kamRegionType?.guid || a.regionTypeId === customerServiceRegionType?.guid || a.regionTypeId === adminRegionType?.guid;
          return a;
        });
        const tree = listToTree(regions, 'guid', 'parentRegionId', this.mapToTreeNode);
        this.backOfficeRegions = tree.filter((a: any) => a.item.data.regionTypeId === backOfficeRegionType?.guid);
        this.serviceRegions = tree.filter((a: any) => a.item.data.regionTypeId === serviceRegionType?.guid);
        const adminRegionList = tree.filter((a: any) => a.item.data.regionTypeId === adminRegionType?.guid);
        const unassignedRegion = adminRegionList.find((a: any) => trim(toLower(a.item.name)) === trim(toLower(Strings.unassignedRegion)));
        if (unassignedRegion) {
          unassignedRegion.item.name = '';
        }
        this.adminRegions = orderBy(adminRegionList, (a: any) => a.item.name, ['asc']);
        this.modalLoader = false;

        const kamUsers = data[2];
        this.filteredKams = this.newKamControl.valueChanges.pipe(
          startWith(''),
          filter((a): a is string => isString(a)),
          distinctUntilChanged(),
          debounceTime(500),
          map((searchText: string) => {
            return searchText.trim().length > 0
              ? kamUsers.filter((kam: any) => kam.name.toLowerCase().indexOf(searchText.toLowerCase()) === 0 && !isNil(kam.guid))
              : [];
          })
        );
      }
    );
  }

  mapToTreeNode(item: any, parent: any) {
    const treeNode = {} as Node;
    treeNode.item = {} as Item;
    treeNode.item.id = item.guid;
    treeNode.item.name = item.name;
    treeNode.item.selectable = item.selectable;
    treeNode.item.data = {
      regionTypeId: item.regionTypeId,
      parentRegion: parent ? { id: parent.guid, name: parent.name, type: parent.regionTypeId } : null,
    };
    treeNode.children = item.children;
    return treeNode;
  }

  trackByFn(_: number, item: any) {
    return item.municipalityId;
  }

  get isFormValid() {
    const kamValue = this.newKamControl.value;
    return (
      this.backOfficeSelected &&
      (!isNil(this.municipality.primaryAdminGuid) || (this.newKamControl.valid && isPlainObject(kamValue) && !isNil((kamValue as any).guid)))
    );
  }

  save() {
    if (this.isFormValid) {
      this.overlayLoader.isLoading = true;
      const save$ = [];

      const backOfficeRegion = this.backOfficeRegionTree?.selectedNodes[0];
      const serviceRegion = this.serviceRegionTree?.selectedNodes[0];
      const adminRegion = this.adminRegionTree?.selectedNodes[0];

      const primaryKamAssignment = {
        municipalities: [this.municipality],
        kam: {
          firstname: (this.newKamControl.value as any)?.firstname,
          lastname: (this.newKamControl.value as any)?.lastname,
          guid: (this.newKamControl.value as any)?.guid,
        },
      };
      this.reassignKam.reassignMunicipalitiesToKam(primaryKamAssignment.municipalities as [], primaryKamAssignment.kam).subscribe();

      const kamRegionData = {
        regionId: backOfficeRegion?.item.id,
        municipalityId: this.municipality.guid,
        modifyUser: this.authService.currentUser.guid,
      };
      save$.push(this.regionMunicipalityService.postMunicipalityRegionMapping(kamRegionData));

      const backOfficeRegionData = {
        regionId: backOfficeRegion?.item.data.parentRegion.id,
        municipalityId: this.municipality.guid,
        modifyUser: this.authService.currentUser.guid,
      };
      save$.push(this.regionMunicipalityService.postMunicipalityRegionMapping(backOfficeRegionData));

      if (serviceRegion) {
        const customerServiceRegionData = {
          regionId: serviceRegion.item.id,
          municipalityId: this.municipality.guid,
          modifyUser: this.authService.currentUser.guid,
        };
        save$.push(this.regionMunicipalityService.postMunicipalityRegionMapping(customerServiceRegionData));

        const serviceRegionData = {
          regionId: serviceRegion.item.data.parentRegion.id,
          municipalityId: this.municipality.guid,
          modifyUser: this.authService.currentUser.guid,
        };
        save$.push(this.regionMunicipalityService.postMunicipalityRegionMapping(serviceRegionData));
      }

      if (adminRegion) {
        const adminRegionData = {
          regionId: adminRegion.item.id,
          municipalityId: this.municipality.guid,
          modifyUser: this.authService.currentUser.guid,
        };
        save$.push(this.regionMunicipalityService.postMunicipalityRegionMapping(adminRegionData));
      } else {
        const q = `?$filter=regionTypeId eq ${find(this.regionTypes, (a) => a.code === 'AM').guid} and name eq '${Strings.unassignedRegion}'`;
        const adminRegion$ = this.regionsService.getAllRegions(q).pipe(
          catchError((err) => {
            if (err.status === 404) {
              return this.regionsService.post({
                regionTypeId: find(this.regionTypes, (a) => a.code === 'AM')?.guid,
                name: Strings.unassignedRegion,
                code: Strings.unassignedRegion,
                isActive: true,
                modifyUser: 'System',
              });
            } else {
              return of([]);
            }
          }),
          switchMap((region: Region | Region[]) => {
            if (!isEmpty(region)) {
              const regionId = Array.isArray(region) ? region[0].guid : region.guid;
              const adminRegionData = {
                regionId: regionId,
                municipalityId: this.municipality.guid,
                modifyUser: this.authService.currentUser.guid,
              };
              return this.regionMunicipalityService.postMunicipalityRegionMapping(adminRegionData);
            } else {
              return of([]);
            }
          })
        );

        save$.push(adminRegion$);
      }
      combineLatest(save$)
        .pipe(
          mergeMap(() =>
            this.regionManagementData
              .getRegionMunicipalityMappingByMunicipalityId(this.municipality.guid)
              .pipe(tap((regionMunicipality) => this.regionManagementData.addRegionMunicipality(regionMunicipality)))
          )
        )
        .subscribe({
          next: () => {
            this.overlayLoader.isLoading = false;
            this.matDialogRef.close(true);
          },
          error: () => {
            this.overlayLoader.isLoading = false;
            this.matDialogRef.close(null);
          },
        });
    }
  }

  cancel() {
    this.matDialogRef.close(false);
  }

  displayKam = (kam: any) => (kam && kam.name ? kam.name : '');
}
